import nodeCrypto from 'node:crypto'
import type { Logger } from 'pino'
import type { ManagedCache } from '@gzl10/nexus-sdk'
import type { MailService } from '../mail/mail.service.js'
import type { RequestInfo } from './auth.types.js'
import type { ModuleContext } from '@gzl10/nexus-sdk'

/**
 * OTP generation options
 */
export interface OTPOptions {
  /** Email address to send OTP to */
  email: string
  /** Type of OTP operation */
  type: 'login' | 'register' | 'reset'
  /** Request metadata (IP, device info) */
  requestInfo?: RequestInfo
  /** TTL in seconds (default 300 = 5 min) */
  ttl?: number
}

/**
 * Email template configuration for OTP
 */
interface OTPEmailTemplate {
  subject: string
  title: string
  message: (code: string) => string
}

/**
 * OTP Manager - Centralizes OTP generation, validation, and email sending.
 *
 * Eliminates code duplication across login, register, and password reset flows.
 * Handles OTP generation, caching, email formatting, and validation.
 */
export class OTPManager {
  private readonly DEFAULT_TTL = 300 // 5 minutes

  /** Max failed attempts before OTP is invalidated */
  private readonly MAX_ATTEMPTS = 5

  constructor(
    private authCache: ManagedCache<number | { code: string; ip: string; attempts?: number }>,
    private mailService: MailService | undefined,
    private logger: Logger,
    private errors?: ModuleContext['core']['errors']
  ) {}

  /**
   * Generates and sends an OTP code via email.
   *
   * @param options - OTP generation options
   * @throws {Error} If OTP generation fails
   * @returns Object with OTP sent status and expiration time
   */
  async generateAndSend(options: OTPOptions): Promise<{ otpSent: boolean; expiresIn: number }> {
    const { email, type, requestInfo, ttl = this.DEFAULT_TTL } = options
    const otpKey = this.getOTPKey(type, email)

    // Fail fast if mail service is not available
    if (!this.mailService) {
      throw new Error('Mail service not available')
    }

    // Reuse existing OTP code if already generated (resend instead of blocking)
    const existingOtp = await this.authCache.get(otpKey) as { code: string; ip: string; attempts?: number } | null
    const code = existingOtp
      ? existingOtp.code
      : nodeCrypto.randomInt(100000, 999999).toString()

    // Store in cache (refreshes TTL on resend)
    await this.authCache.set(otpKey, { code, ip: requestInfo?.ip ?? '' }, ttl)

    // Send email (throw on failure so caller knows it wasn't delivered)
    try {
      const template = this.getTemplate(type)
      await this.mailService.send({
        to: email,
        subject: template.subject,
        title: template.title,
        message: template.message(code)
      })
      this.logger.debug({ email, type, ttl, resend: !!existingOtp }, 'OTP sent successfully')
    } catch (mailError) {
      this.logger.warn({ err: mailError, email, type }, 'Failed to send OTP email')
      // Clean up cached OTP since user won't receive it
      await this.authCache.delete(otpKey)
      throw new Error('Failed to send verification email')
    }

    return { otpSent: true, expiresIn: ttl }
  }

  /**
   * Validates an OTP code.
   *
   * @param email - User email address
   * @param otp - OTP code to validate
   * @param type - OTP operation type
   * @returns True if OTP is valid, false otherwise
   */
  async validate(email: string, otp: string, type: OTPOptions['type']): Promise<boolean> {
    const otpKey = this.getOTPKey(type, email)
    const storedOtp = await this.authCache.get(otpKey) as { code: string; ip: string; attempts?: number } | null

    if (!storedOtp) {
      this.logger.debug({ email, type }, 'OTP not found for email')
      return false
    }

    if (storedOtp.code !== otp) {
      storedOtp.attempts = (storedOtp.attempts ?? 0) + 1
      if (storedOtp.attempts >= this.MAX_ATTEMPTS) {
        await this.authCache.delete(otpKey)
        this.logger.warn({ email, type, attempts: storedOtp.attempts }, 'OTP invalidated after max failed attempts')
      } else {
        this.logger.warn({ email, type, attempts: storedOtp.attempts }, 'Invalid OTP code attempted')
      }
      return false
    }

    return true
  }

  /**
   * Clears an OTP from cache after successful validation.
   *
   * @param email - User email address
   * @param type - OTP operation type
   */
  async clear(email: string, type: OTPOptions['type']): Promise<void> {
    const otpKey = this.getOTPKey(type, email)
    await this.authCache.delete(otpKey)
    this.logger.debug({ email, type }, 'OTP cleared from cache')
  }

  /**
   * Checks if an OTP exists for a given email and type.
   *
   * @param email - User email address
   * @param type - OTP operation type
   * @returns True if OTP exists, false otherwise
   */
  async exists(email: string, type: OTPOptions['type']): Promise<boolean> {
    const otpKey = this.getOTPKey(type, email)
    return (await this.authCache.get(otpKey)) !== null
  }

  /**
   * Gets the cache key for an OTP.
   *
   * @param type - OTP operation type
   * @param email - User email address
   * @returns Cache key string
   */
  private getOTPKey(type: OTPOptions['type'], email: string): string {
    return `${type}_otp:${email}`
  }

  /**
   * Gets the email template for an OTP type.
   *
   * @param type - OTP operation type
   * @returns Email template configuration
   */
  private getTemplate(type: OTPOptions['type']): OTPEmailTemplate {
    const codeDisplay = (code: string) => `
      <div style="text-align: center; margin: 24px 0;">
        <span style="font-size: 32px; font-weight: bold; letter-spacing: 8px; font-family: monospace; background: #f3f4f6; padding: 16px 24px; border-radius: 8px; display: inline-block;">${code}</span>
      </div>
      <p style="color: #6b7280; font-size: 14px;">This code expires in 5 minutes. If you didn't request this, ignore this email.</p>
    `

    switch (type) {
      case 'login':
        return {
          subject: 'Login Verification Code',
          title: 'Verify Your Identity',
          message: (code) => `<p>Your verification code is:</p>${codeDisplay(code)}`
        }

      case 'register':
        return {
          subject: 'Verify Your Email',
          title: 'Complete Your Registration',
          message: (code) => `<p>Your verification code is:</p>${codeDisplay(code)}`
        }

      case 'reset':
        return {
          subject: 'Password Reset Code',
          title: 'Reset Your Password',
          message: (code) => `<p>Your password reset code is:</p>${codeDisplay(code)}`
        }

      default:
        // Fallback (never reached with correct typing)
        return {
          subject: 'Verification Code',
          title: 'Verify Your Email',
          message: (code) => `<p>Your verification code is:</p>${codeDisplay(code)}`
        }
    }
  }
}
